import React, { useState, useEffect, useRef, useCallback } from "react";

// --- Helper Hook for Draggability ---
const useDraggable = (id) => {
  const [position, setPosition] = useState({ top: 5, left: window.innerWidth - 210, isDragging: false });
  const dragRef = useRef(null);

  const onMouseDown = useCallback((e) => {
    if (dragRef.current && dragRef.current.contains(e.target)) {
      setPosition((pos) => ({ ...pos, isDragging: true }));
      dragRef.current.offset = {
        x: e.clientX - dragRef.current.getBoundingClientRect().left,
        y: e.clientY - dragRef.current.getBoundingClientRect().top,
      };
    }
  }, []);

  const onMouseUp = useCallback(() => {
    setPosition((pos) => ({ ...pos, isDragging: false }));
  }, []);

  const onMouseMove = useCallback(
    (e) => {
      if (position.isDragging && dragRef.current) {
        const newX = e.clientX - dragRef.current.offset.x;
        const newY = e.clientY - dragRef.current.offset.y;
        setPosition((pos) => ({ ...pos, top: newY, left: newX }));
      }
    },
    [position.isDragging]
  );

  // --- Added touch support (minimal, mirrors mouse logic) ---
  const onTouchStart = useCallback((e) => {
    if (dragRef.current && dragRef.current.contains(e.target)) {
      const touch = e.touches && e.touches[0];
      if (!touch) return;
      setPosition((pos) => ({ ...pos, isDragging: true }));
      dragRef.current.offset = {
        x: touch.clientX - dragRef.current.getBoundingClientRect().left,
        y: touch.clientY - dragRef.current.getBoundingClientRect().top,
      };
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    setPosition((pos) => ({ ...pos, isDragging: false }));
  }, []);

  const onTouchMove = useCallback(
    (e) => {
      if (position.isDragging && dragRef.current) {
        const touch = e.touches && e.touches[0];
        if (!touch) return;
        const newX = touch.clientX - dragRef.current.offset.x;
        const newY = touch.clientY - dragRef.current.offset.y;
        setPosition((pos) => ({ ...pos, top: newY, left: newX }));
        // prevent scroll-jank while dragging
        e.preventDefault();
      }
    },
    [position.isDragging]
  );
  // --- end touch additions ---

  const onDoubleClick = useCallback(() => {
    setPosition({ top: 80, left: window.innerWidth - 220, isDragging: false });
  }, []);

  useEffect(() => {
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    // touch listeners (passive: false so we can preventDefault during drag)
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd);
    document.addEventListener("touchcancel", onTouchEnd);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);

      document.removeEventListener("touchmove", onTouchMove, { passive: false });
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [onMouseMove, onMouseUp, onTouchMove, onTouchEnd]);

  return { ref: dragRef, position, onMouseDown, onDoubleClick, onTouchStart };
};

// --- GoalsMultiSelect Component ---
// This is the custom dropdown for selecting multiple goals.
export const GoalsMultiSelect = ({ selectedGoals, onChange, tabIndex }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const goalOptions = [
    // Required rehab-focused goals
    { value: "reduce_pain", text: "Reduce Pain" },
    { value: "less_stiff", text: "Reduce Stiffness" },
    { value: "improve_strength", text: "Improve Strength" },
    { value: "improve_flexibility", text: "Improve Flexibility" },
    { value: "daily_tasks", text: "Make Daily Tasks Easier" },
    { value: "balance", text: "Improve Balance & Stability" },
    { value: "picking_up", text: "Picking Up Things" },

    // Existing additional goals
    { value: "sleep", text: "Improve Sleep Quality" },
    { value: "walking", text: "Improve Walking & Endurance" },
    { value: "lifting", text: "Make Bending & Lifting Easier" },
    { value: "posture", text: "Improve Sitting & Standing Posture" },
    { value: "rotational_sports", text: "Return to Sports (Golf, Tennis, etc.)" },
    { value: "overhead_sports", text: "Return to Sports (Basketball, Swimming, etc.)" },
    { value: "running", text: "Running, Cycling, etc." },
    { value: "fitness", text: "Fitness: Gym, CrossFit, etc." },
  ];

  const toggleOption = (goal) => {
    const isSelected = selectedGoals.some((g) => g.value === goal.value);
    if (isSelected) {
      onChange(selectedGoals.filter((g) => g.value !== goal.value));
    } else {
      onChange([...selectedGoals, goal]);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const styles = {
    container: { position: "relative" },
    selectBox: { border: "1px solid #d1d5db", borderRadius: "0.375rem", padding: "0.5rem 0.75rem", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" },
    selectBoxOpen: { borderColor: "#0d9488", boxShadow: "0 0 0 2px #0d9488" },
    selectedItems: { display: "flex", flexWrap: "wrap", gap: "0.25rem", flexGrow: 1 },
    itemTag: { backgroundColor: "#ccfbf1", color: "#134e4a", padding: "0.125rem 0.5rem", borderRadius: "0.25rem", fontSize: "0.875rem", fontWeight: 500, display: "flex", alignItems: "center" },
    removeTag: { marginLeft: "0.25rem", cursor: "pointer", fontWeight: "bold" },
    optionsContainer: { display: isOpen ? "block" : "none", position: "absolute", top: "100%", left: 0, right: 0, backgroundColor: "white", border: "1px solid #d1d5db", borderTop: "none", borderRadius: "0 0 0.375rem 0.375rem", maxHeight: "200px", overflowY: "auto", zIndex: 10 },
    option: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.75rem", cursor: "pointer" },
    optionSelected: { backgroundColor: "#ccfbf1" },
    label: { fontWeight: 500 },
    labelSelected: { fontWeight: 600 },
    checkbox: { width: "1rem", height: "1rem", border: "2px solid #9ca3af", borderRadius: "0.25rem", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    checkboxSelected: { borderColor: "#0d9488", backgroundColor: "#0d9488" },
    tick: { display: "block", width: "5px", height: "9px", border: "solid white", borderWidth: "0 2px 2px 0", transform: "rotate(45deg)" },
  };

  return (
    <div style={styles.container} ref={containerRef}>
      <div
        style={{ ...styles.selectBox, ...(isOpen && styles.selectBoxOpen) }}
        onClick={() => setIsOpen(!isOpen)}
        tabIndex={tabIndex}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
      >
        <div style={styles.selectedItems}>
          {selectedGoals.length === 0 ? (
            <span style={{ color: "#9ca3af" }}>Select your goals...</span>
          ) : (
            selectedGoals.map((goal) => (
              <div key={goal.value} style={styles.itemTag}>
                {goal.text}
                <span
                  style={styles.removeTag}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleOption(goal);
                  }}
                >
                  &times;
                </span>
              </div>
            ))
          )}
        </div>
        <svg style={{ width: "1.25rem", height: "1.25rem", color: "#9ca3af" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </div>
      <div style={styles.optionsContainer}>
        {goalOptions.map((option) => {
          const isSelected = selectedGoals.some((g) => g.value === option.value);
          return (
            <div
              key={option.value}
              style={{ ...styles.option, ...(isSelected && styles.optionSelected) }}
              onClick={() => toggleOption(option)}
              tabIndex={tabIndex} // Add this
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleOption(option);
                }
              }}
            >
              <span style={{ ...styles.label, ...(isSelected && styles.labelSelected) }}>{option.text}</span>
              <div style={{ ...styles.checkbox, ...(isSelected && styles.checkboxSelected) }}>{isSelected && <div style={styles.tick}></div>}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- ProgressChecklist Component ---
// This is the floating, draggable progress widget.
export const ProgressChecklist = ({ items }) => {
  const { ref, position, onMouseDown, onDoubleClick, onTouchStart } = useDraggable("progress-checklist");

  const styles = {
    checklist: {
      position: "fixed",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      backdropFilter: "blur(4px)",
      border: "1px solid #e2e8f0",
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
      borderRadius: "0.5rem",
      zIndex: 50,
      width: "192px",
      top: `${position.top}px`,
      left: `${position.left}px`,
      transition: position.isDragging ? "none" : "box-shadow 0.2s ease-in-out",
    },
    dragging: {
      cursor: "grabbing",
      boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
    },
    dragHandle: {
      padding: "0.5rem",
      borderBottom: "1px solid #e2e8f0",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      cursor: "move",
    },
    title: { fontWeight: "bold", fontSize: "0.875rem", color: "#334155" },
    icon: { width: "1rem", height: "1rem", color: "#9ca3af" },
    body: { padding: "0.75rem", display: "flex", flexDirection: "column", gap: "0.5rem" },
    item: { display: "flex", alignItems: "center", gap: "0.375rem" },
    itemIcon: { width: "1rem", height: "1rem" },
    itemText: { fontSize: "0.75rem", fontWeight: 500 },
    textComplete: { color: "#065f46", textDecoration: "line-through" },
    textIncomplete: { color: "#475569" },
  };

  return (
    <div
      ref={ref}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart} // <-- added
      onDoubleClick={onDoubleClick}
      style={{ ...styles.checklist, ...(position.isDragging && styles.dragging) }}
    >
      <div style={styles.dragHandle}>
        <h4 style={styles.title}>Your Progress</h4>
        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
        </svg>
      </div>
      <div style={styles.body}>
        {items.map((item) => (
          <div key={item.id} style={styles.item}>
            <span style={{ ...styles.itemIcon, color: item.completed ? "#10b981" : "#94a3b8" }}>
              {item.completed ? (
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
              )}
            </span>
            <span style={{ ...styles.itemText, ...(item.completed ? styles.textComplete : styles.textIncomplete) }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
